/**
 * Address Service
 *
 * Handles CRUD operations for app.addresses table
 *
 * @module AddressService
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { APP_SCHEMA_ENDPOINTS, buildUrlWithParams } from '../api';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import type { AddressCreate, AddressRead, AddressUpdate } from '../models/address.types';

@Injectable({
  providedIn: 'root',
})
export class AddressService {
  private http = inject(HttpClient);

  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  private handleError = (error: unknown): Observable<never> => {
    console.error('❌ AddressService Error:', error);
    if (error && typeof error === 'object' && 'error' in error) {
      console.error('❌ Error details:', (error as any).error);
    }
    throw error;
  };

  /**
   * Create address
   */
  createAddress(addressData: AddressCreate): Observable<AddressRead> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.addresses.create);

    console.log('🔍 Creating address with data:', JSON.stringify(addressData, null, 2));
    console.log('🔍 URL:', url);

    return this.http
      .post<AddressRead>(url, addressData, {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Get address by ID
   */
  getAddress(addressId: number): Observable<AddressRead> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.addresses.getById(addressId));

    return this.http
      .get<AddressRead>(url, { headers: this.createHeaders() })
      .pipe(catchError(this.handleError));
  }

  /**
   * Update address
   */
  updateAddress(addressId: number, addressData: AddressUpdate): Observable<AddressRead> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.addresses.update(addressId));

    return this.http
      .put<AddressRead>(url, addressData, {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Delete address
   */
  deleteAddress(addressId: number): Observable<void> {
    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.addresses.delete(addressId));

    return this.http
      .delete<void>(url, { headers: this.createHeaders() })
      .pipe(catchError(this.handleError));
  }
}
